import ItemView from "./ItemView";
import { assign, Sentinel } from "./common";
import View from "./View";
import ErrorView from "./ErrorView";
import SpinnerView from "./SpinnerView";

const FileView = ItemView.extend(
  /** @lends FileView.prototype */
  {
    /**
     * @param {FileModel} model
     * @param {object} opts
     * @param {number} [opts.mouseDragThreshold=10] how far mouse must travel to initiate dragging
     * @param {number} [opts.touchDragThreshold=10] how far touch must travel to cancel dragging
     * @param {number} [opts.touchDragDelay=200] for how long the image has to be held to begin dragging
     * @constructs
     * @extends ItemView
     */
    constructor(model, opts) {
      ItemView.call(this, model, opts);

      /**
       * used for click prevention when dragging
       * @type {boolean}
       * @protected
       */
      this.ignoreNextClick = false;

      this.constructDOM();
    },

    markDragged(state) {
      ItemView.prototype.markDragged.call(this, state);
      if(state) this.ignoreNextClick = true;
      else setTimeout(() => { this.ignoreNextClick = false }, 1);
    },

    /**
     * Hook for creating view's DOM structure
     * @protected
     */
    constructDOM() {
      this.progressSentinel = new Sentinel();
      this.errorSentinel = new Sentinel();

      const delBtnHTML = this.opts.template.parse("del_btn", { id: this.id });

      this.setHTML(this.opts.template.parse("file_item", {
        id: this.id,
        progress: this.progressSentinel.string,
        error: this.errorSentinel.string,
        del_btn: delBtnHTML,
      }));

      this.progressView = this.createProgressView();
      this.errorView = this.createErrorView();
    },

    /**
     * Factory method for creating the progress view (see {@link SpinnerView})
     * @protected
     */
    createProgressView() {
      return new SpinnerView(this.model, this.opts);
    },

    /**
     * Factory method for creating the error view (see {@link ErrorView})
     * @protected
     */
    createErrorView() {
      return new ErrorView(this.model, this.opts);
    },

    /**
     * Indicate models state
     * @param {*} state - one of {@link FileStates}
     * @protected
     */
    setFileState(state) {
      this.removeClass("up_" + this.model.state.toLowerCase());
      this.addClass("up_" + state.toLowerCase());
      const statusEl = this.getElement("status");
      if(statusEl) statusEl.textContent = state;
    },

    /**
     * Handle delete button click
     * @protected
     */
    handleDeleteClick() {
      if(this.ignoreNextClick) {
        this.ignoreNextClick = false;
        return;
      }
      this.model.delete();
    },

    onMount() {
      const wrapEl = this.getElement("item");

      this.bindPointerListeners(wrapEl);

      this.getElement("del_file").addEventListener("click", evt => { this.handleDeleteClick(evt); });

      this.progressView.insertBefore(this.progressSentinel.getReference());
      this.progressSentinel.remove(); // not necessary, but the element is no longer needed

      this.errorView.insertBefore(this.errorSentinel.getReference());
      this.errorSentinel.remove(); // not necessary, but the element is no longer needed

      this.setFileState(this.model.state);
      this.listenTo(this.model, "state_changed", ({ state }) => {
        this.setFileState(state);
        this.updateDownloadHref();
      });

      this.listenTo(this.model, "delete_failed", () => { this.show(); });

      this.listenTo(this.model, "destroy", () => { this.destroy(); });

      this.updateDownloadHref();
    },

    /**
     * Bind listeners for click and drag (mouse + touch) on given element
     * @param {HTMLElement} el
     * @protected
     */
    bindPointerListeners(el) {
      el.addEventListener("mousedown", evt => this.handleMouseDown(evt));
      el.addEventListener("touchstart", evt => this.handleTouchStart(evt));
    },

    /**
     * Handle mousedown event for dragstart/click event detection
     * @private
     */
    handleMouseDown(evt) {
      const origEvt = evt;
      const origX = evt.clientX;
      const origY = evt.clientY;

      const mousemove = evt => {
        const dx = evt.clientX - origX;
        const dy = evt.clientY - origY;
        const sqrDist = dx * dx + dy * dy;
        const thr = this.opts.mouseDragThreshold;

        if(sqrDist >= thr * thr) {
          // dragged far enough
          cancel();
          this.emit("dragstart", { event: origEvt, x: evt.clientX, y: evt.clientY, touch: false });
        }
      };

      function cancel() {
        document.removeEventListener("mousemove", mousemove);
        document.removeEventListener("mouseup", mouseup);
      }

      const mouseup = evt => {
        // didn't drag far enough
        cancel();
        this.emit("click", { event: evt, touch: false });
      };

      document.addEventListener("mousemove", mousemove);
      document.addEventListener("mouseup", mouseup);
    },

    /**
     * Handle touchstart event for dragstart/click event detection
     * @private
     */
    handleTouchStart(evt) {
      if(!evt.touches || evt.touches.length != 1) return;

      const origEvt = evt;
      const origX = evt.touches[0].clientX;
      const origY = evt.touches[0].clientY;

      const initDrag = () => {
        cancel();
        this.emit("dragstart", {
          event: origEvt,
          x: origEvt.touches[0].clientX,
          y: origEvt.touches[0].clientY,
          touch: true
        });
      };

      const to = setTimeout(initDrag, this.opts.touchDragDelay);

      const touchmove = evt => {
        const dx = evt.touches[0].clientX - origX;
        const dy = evt.touches[0].clientY - origY;
        const sqrDist = dx * dx + dy * dy;
        const thr = this.opts.touchDragThreshold;

        if(sqrDist >= thr * thr) {
          // finger travelled too far, cancel drag
          cancel();
        }
      };

      function contextmenu(evt) {
        evt.preventDefault();
      }

      function cancel() {
        document.removeEventListener("touchmove", touchmove);
        document.removeEventListener("touchend", touchend);
        document.removeEventListener("touchcancel", touchend);
        document.removeEventListener("contextmenu", contextmenu);
        clearTimeout(to);
      }

      const touchend = evt => {
        cancel();
        this.emit("click", { event: evt, touch: true });
      };

      document.addEventListener("touchmove", touchmove);
      document.addEventListener("touchend", touchend);
      document.addEventListener("touchcancel", touchend);
      document.addEventListener("contextmenu", contextmenu);
    },

    getDefaultOptions() {
      return assign(View.prototype.getDefaultOptions.call(this), {
        mouseDragThreshold: 10,
        touchDragThreshold: 10,
        touchDragDelay: 200
      });
    },

    updateDownloadHref() {
      const fnameEl = this.getElement("fname");
      if(fnameEl) {
        fnameEl.textContent = this.model.getName();
        if (fnameEl.tagName === "A") fnameEl.href = this.model.getDownloadLink();
      }
    }
  });

export default FileView;
